/**
 * Web-specific file export utilities for browser downloads
 * This provides fallback functionality when mobile plugins are not available
 */

export interface WebFileExportOptions {
  filename: string;
  data: string | Blob;
  mimeType: string;
}

/**
 * Download a file in the browser using the download API
 */
export const downloadFileInBrowser = (options: WebFileExportOptions): Promise<{ success: boolean; error?: string }> => {
  return new Promise((resolve) => {
    try {
      const { filename, data, mimeType } = options;
      
      // Create blob if data is string
      let blob: Blob;
      if (typeof data === 'string') {
        // Check if it's base64 data
        if (data.startsWith('data:')) {
          // It's a data URL, extract the base64 part
          const base64Data = data.split(',')[1];
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          blob = new Blob([bytes], { type: mimeType });
        } else {
          // Regular string data
          blob = new Blob([data], { type: mimeType });
        }
      } else {
        blob = data;
      }
      
      // Create download URL
      const url = URL.createObjectURL(blob);
      
      // Create temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      // Add to document, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);
      
      resolve({ success: true });
    } catch (error) {
      
      resolve({ 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
    }
  });
};

/**
 * Create and download a CSV file
 */
export const downloadCSV = (filename: string, csvData: string): Promise<{ success: boolean; error?: string }> => {
  return downloadFileInBrowser({
    filename: filename.endsWith('.csv') ? filename : `${filename}.csv`,
    data: csvData,
    mimeType: 'text/csv;charset=utf-8'
  });
};

/**
 * Create and download a PDF file
 */
export const downloadPDF = (filename: string, pdfData: string | Blob): Promise<{ success: boolean; error?: string }> => {
  return downloadFileInBrowser({
    filename: filename.endsWith('.pdf') ? filename : `${filename}.pdf`,
    data: pdfData,
    mimeType: 'application/pdf'
  });
};

/**
 * Create and download a JSON file
 */
export const downloadJSON = (filename: string, jsonData: object): Promise<{ success: boolean; error?: string }> => {
  const jsonString = JSON.stringify(jsonData, null, 2);
  return downloadFileInBrowser({
    filename: filename.endsWith('.json') ? filename : `${filename}.json`,
    data: jsonString,
    mimeType: 'application/json'
  });
};

/**
 * Check if the browser supports file downloads
 */
export const isFileDownloadSupported = (): boolean => {
  return typeof document !== 'undefined' && 
         typeof URL !== 'undefined' && 
         typeof URL.createObjectURL === 'function';
};

/**
 * Open file in new tab instead of downloading (useful for PDFs)
 */
export const openFileInNewTab = (data: string | Blob, mimeType: string): Promise<{ success: boolean; error?: string }> => {
  return new Promise((resolve) => {
    try {
      let blob: Blob;
      if (typeof data === 'string') {
        blob = new Blob([data], { type: mimeType });
      } else {
        blob = data;
      }
      
      const url = URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      
      if (newWindow) {
        // Clean up after a delay
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 10000);
        resolve({ success: true });
      } else {
        resolve({ success: false, error: 'Popup blocked' });
      }
    } catch (error) {
      resolve({ 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
    }
  });
}; 